import {
  AlertTriangle,
  Loader2,
  Mail,
  RefreshCw,
  UserRoundX,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { UserAvatar } from "@/components/ui/user-avatar";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { useI18n } from "@/hooks/useI18n";
import { useMoneyCopy } from "@/lib/i18n/money-copy";
import { useAdvisorDetail } from "@/hooks/useAdvisorDetail";
import { formatPlanAmount } from "@/lib/builder/utils/payment-format";
import { DetailItinerariesTable } from "@/lib/dashboard/shared/DetailItinerariesTable";
import { DetailKpiTile } from "@/lib/dashboard/shared/DetailKpiTile";
import { DetailSheetTabs } from "@/lib/dashboard/shared/DetailSheetTabs";
import {
  DetailCashflowChart,
  DetailDestinationsChart,
} from "@/lib/dashboard/shared/DetailCharts";
import {
  AdvisorDetailClient,
  AdvisorDetailKpis,
  AgencyDashboardPeriod,
} from "@/integrations/supabase/rpc-types";
import type { TranslationKey } from "@/types/i18n.types";
import { PlatformFeeDueBanner } from "@/lib/payments/components/PlatformFeeDueBanner";

function roleLabel(role: string, t: (key: TranslationKey) => string): string {
  switch (role) {
    case "travel_designer":
      return t("role.travelAdvisor");
    case "travel_advisor_member":
      return t("role.travelAdvisorMember");
    default:
      return t("role.unknown");
  }
}

interface AdvisorKpiGridProps {
  kpis: AdvisorDetailKpis;
  /**
   * Dalla sezione `admin` della RPC, che il database manda solo all'admin: quanto markup
   * ha prodotto una persona e' un numero di piattaforma. Assente = casella assente, senza
   * controlli di ruolo qui.
   */
  marginTotal?: number;
}

function AdvisorKpiGrid({ kpis, marginTotal }: AdvisorKpiGridProps) {
  const { t } = useI18n();
  const money = useMoneyCopy();
  const showMargin = marginTotal !== undefined;

  return (
    /*
      Stesso ordine della scheda cliente -- quantita', pagato, in attesa, totale -- con il
      margine in coda: e' l'unico numero che non fa parte del conto, quindi non si infila
      in mezzo alla sequenza e non ruba l'enfasi al totale. Cinque caselle e non quattro perche'
      togliere il margine da qui avrebbe tolto alla scheda la sua misura di performance.

      Le etichette del denaro vengono da useMoneyCopy(), come ovunque: si legge dal lato
      di chi paga anche qui, perche' e' la stessa cifra della scheda cliente.
    */
    <div
      className={`grid grid-cols-2 gap-2 ${showMargin ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}
    >
      <DetailKpiTile
        label={t("dashboard.overview.advisorDetail.kpiRequests")}
        value={`${kpis.active_count} / ${kpis.requests_count}`}
        info={t("dashboard.overview.advisorDetail.kpiRequestsInfo")}
      />
      <DetailKpiTile
        label={t(`${money}.paid`)}
        value={formatPlanAmount(kpis.paid_amount)}
        info={t("dashboard.overview.advisorDetail.kpiPaidInfo")}
        tone="success"
      />
      <DetailKpiTile
        label={t(`${money}.pending`)}
        value={formatPlanAmount(kpis.remaining_amount)}
        info={t("dashboard.overview.advisorDetail.kpiRemainingInfo")}
        tone={kpis.overdue_amount > 0 ? "danger" : "neutral"}
      />
      <DetailKpiTile
        label={t(`${money}.total`)}
        value={formatPlanAmount(kpis.total_amount)}
        info={t("dashboard.overview.advisorDetail.kpiClosedValueInfo")}
        emphasis
      />
      {showMargin && (
        <DetailKpiTile
          label={t("dashboard.overview.advisorDetail.kpiMargin")}
          value={formatPlanAmount(marginTotal)}
          info={t("dashboard.overview.advisorDetail.kpiMarginInfo")}
        />
      )}
    </div>
  );
}

interface AdvisorClientsCardProps {
  clients: AdvisorDetailClient[];
  onOpenClient?: (clientKey: string) => void;
}

/**
 * I clienti del consulente, per valore portato.
 *
 * Le righe rimandano alla tab Clienti quando il chiamante sa aprirla: la `client_key` e'
 * la stessa delle due viste, quindi il salto e' diretto e non passa da una ricerca.
 */
function AdvisorClientsCard({ clients, onOpenClient }: AdvisorClientsCardProps) {
  const { t } = useI18n();

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          {t("dashboard.overview.advisorDetail.clientsTitle")}
          <InfoTooltip text={t("dashboard.overview.advisorDetail.clientsInfo")} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {clients.length === 0 ? (
          <div className="flex items-center justify-center h-24 text-sm text-gray-400">
            {t("dashboard.overview.advisorDetail.clientsEmpty")}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {clients.slice(0, 8).map((client) => {
              const name = client.client_name || client.email || client.client_key;
              const row = (
                <>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-gray-800">{name}</p>
                    <p className="text-xs text-gray-400">
                      {t("dashboard.overview.advisorDetail.clientRequests", {
                        count: client.requests_count,
                      })}
                    </p>
                  </div>
                  <span className="text-gray-600 flex-shrink-0 tabular-nums">
                    {formatPlanAmount(client.closed_value)}
                  </span>
                </>
              );

              return (
                <li key={client.client_key} className="text-sm">
                  {onOpenClient ? (
                    <button
                      type="button"
                      onClick={() => onOpenClient(client.client_key)}
                      className="flex items-center gap-3 w-full py-2 text-left hover:bg-gray-50 rounded px-1 -mx-1"
                    >
                      {row}
                    </button>
                  ) : (
                    <div className="flex items-center gap-3 py-2">{row}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

interface AdvisorDetailSheetProps {
  /** `null` chiude il pannello. */
  advisorId: string | null;
  onOpenChange: (open: boolean) => void;
  period: AgencyDashboardPeriod;
  periodOnly: boolean;
  onPeriodOnlyChange: (value: boolean) => void;
  /** Anagrafica gia' nota dalla riga: riempie l'intestazione mentre la scheda carica. */
  fallbackName?: string;
  fallbackEmail?: string | null;
  /** Salto alla scheda del cliente. Assente se la tab Clienti non e' raggiungibile. */
  onOpenClient?: (clientKey: string) => void;
  /**
   * La scheda si apre sopra una Modal dell'app (es. il dettaglio di una travel
   * request). Le Modal vivono in un portale a z-index 60+, quindi pannello e velo
   * vanno alzati sopra quella soglia o finirebbero sotto la modale che li ha aperti.
   */
  aboveModal?: boolean;
}

/**
 * Scheda del singolo consulente: da dove vengono i quattro numeri della sua riga.
 *
 * Gemella della scheda cliente, stessi componenti e stessa larghezza: chi ha imparato a
 * leggere l'una non deve reimparare l'altra.
 */
export function AdvisorDetailSheet({
  advisorId,
  onOpenChange,
  period,
  periodOnly,
  onPeriodOnlyChange,
  fallbackName,
  fallbackEmail,
  onOpenClient,
  aboveModal = false,
}: AdvisorDetailSheetProps) {
  const { t } = useI18n();
  const { data, loading, error, notFound, refresh } = useAdvisorDetail({
    advisorId,
    period,
    periodOnly,
  });

  const advisor = data?.advisor;
  const name = advisor
    ? [advisor.first_name, advisor.last_name].filter(Boolean).join(" ") ||
      advisor.email ||
      advisor.advisor_id
    : (fallbackName ?? "");
  const email = advisor?.email ?? fallbackEmail ?? null;

  return (
    <Sheet open={advisorId !== null} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={`sm:max-w-3xl w-full overflow-y-auto p-0 ${aboveModal ? "z-[80]" : ""}`}
        overlayClassName={aboveModal ? "z-[80]" : undefined}
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-gray-100 space-y-3">
          <div className="flex items-start gap-3">
            <UserAvatar
              firstName={advisor?.first_name}
              lastName={advisor?.last_name}
              fullName={name}
              email={email}
              imageUrl={advisor?.avatar_url}
              size="lg"
            />
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-left truncate">{name || "—"}</SheetTitle>
              <SheetDescription asChild>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm">
                  {advisor && (
                    <Badge variant="secondary">{roleLabel(advisor.role, t)}</Badge>
                  )}
                  {email && (
                    <a
                      href={`mailto:${email}`}
                      className="inline-flex items-center gap-1.5 text-gray-600 hover:text-orange-600"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      {email}
                    </a>
                  )}
                  {data && (
                    <span className="text-gray-500">
                      {t("dashboard.overview.advisorDetail.clientsCount", {
                        count: data.stats.kpis.clients_count,
                      })}
                    </span>
                  )}
                </div>
              </SheetDescription>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="advisor-period-only"
              checked={periodOnly}
              onCheckedChange={onPeriodOnlyChange}
            />
            <Label
              htmlFor="advisor-period-only"
              className="text-sm font-normal text-gray-600 cursor-pointer"
            >
              {t("dashboard.overview.clients.periodOnlyToggle")}
            </Label>
            <InfoTooltip text={t("dashboard.overview.advisorDetail.periodOnlyInfo")} />
          </div>
        </SheetHeader>

        <div className="px-5 py-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 py-16 text-gray-500 justify-center">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">
                {t("dashboard.overview.advisorDetail.loading")}
              </span>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-50 text-red-500">
                <XCircle className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-900">
                  {t("dashboard.overview.errorTitle")}
                </p>
                <p className="text-sm text-gray-500">
                  {t("dashboard.overview.errorDescription")}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => refresh()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t("dashboard.retry")}
              </Button>
            </div>
          )}

          {!loading && !error && notFound && (
            <div className="flex flex-col items-center gap-2 py-12 text-center text-gray-500">
              <UserRoundX className="h-8 w-8 text-gray-300" />
              <p className="text-sm">
                {t("dashboard.overview.advisorDetail.notFound")}
              </p>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Gli avvisi stanno sopra le tab: "ho commissioni da pagare o no" e "c'e'
                  uno scaduto" sono domande a cui la scheda risponde qualunque tab sia
                  aperta. Il dettaglio per rata resta nelle righe espanse. */}
              <PlatformFeeDueBanner amount={data.stats.kpis.platform_fee_due_amount} />

              {data.stats.kpis.overdue_amount > 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  {t("dashboard.overview.advisorDetail.overdueBanner", {
                    amount: formatPlanAmount(data.stats.kpis.overdue_amount),
                  })}
                </div>
              )}

              <DetailSheetTabs
                itinerariesCount={data.itineraries.length}
                stats={
                  <>
                    <AdvisorKpiGrid
                      kpis={data.stats.kpis}
                      marginTotal={data.admin?.margin_total}
                    />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <DetailCashflowChart data={data.stats.cashflow} />
                      <DetailDestinationsChart
                        data={data.stats.destinations}
                        title={t("dashboard.overview.advisorDetail.destinationsTitle")}
                        info={t("dashboard.overview.advisorDetail.destinationsInfo")}
                      />
                    </div>
                    <AdvisorClientsCard clients={data.stats.clients} onOpenClient={onOpenClient} />
                  </>
                }
                itineraries={
                  <DetailItinerariesTable
                    itineraries={data.itineraries}
                    subtitle="client"
                  />
                }
              />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default AdvisorDetailSheet;
